import * as React from "react";
import {
  Link,
  createFileRoute,
  redirect,
  useRouter,
} from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { HTTPError } from "ky";
import {
  BanIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CircleAlertIcon,
  CircleCheckIcon,
  KeyRoundIcon,
  RotateCcwIcon,
  ShieldAlertIcon,
  Trash2Icon,
  Undo2Icon,
  UsersIcon,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import type {
  AffectedEntity,
  AffectedKey,
  GatewayKeyConflictAction,
  GatewayKeyResolution,
  PendingPolicyConflict,
} from "@/lib/policy-conflicts";
import {
  clearPendingPolicyConflict,
  loadPendingPolicyConflict,
  replayPendingPolicyConflict,
} from "@/lib/policy-conflicts";

type KeyStatus = "propagated" | "revoked" | "skipped";

const STATUS_TO_ACTION: Record<KeyStatus, GatewayKeyConflictAction> = {
  propagated: "add_entities",
  revoked: "delete",
  skipped: "skip",
};

const ACTION_TO_STATUS: Record<GatewayKeyConflictAction, KeyStatus> = {
  add_entities: "propagated",
  delete: "revoked",
  skip: "skipped",
};

interface AffectedUserGroup {
  id: string;
  fullName: string;
  email: string;
  keys: Array<AffectedKey>;
}

export const Route = createFileRoute("/_app/resolve-conflicts/")({
  component: RouteComponent,
  beforeLoad: () => {
    if (!loadPendingPolicyConflict()) {
      throw redirect({ to: "/policies" });
    }
  },
});

function groupAffectedKeysByUser(
  keys: Array<AffectedKey>,
): Array<AffectedUserGroup> {
  const map = new Map<string, AffectedUserGroup>();
  for (const k of keys) {
    const existing = map.get(k.user_id);
    if (existing) existing.keys.push(k);
    else
      map.set(k.user_id, {
        id: k.user_id,
        fullName: k.user_name,
        email: k.user_email,
        keys: [k],
      });
  }
  return Array.from(map.values());
}

function getInitials(name: string) {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

type FilterValue = "all" | "needs-action" | "completed";

function RouteComponent() {
  const router = useRouter();
  const qc = useQueryClient();

  const [pending, setPending] = React.useState<PendingPolicyConflict | null>(
    () => loadPendingPolicyConflict(),
  );

  const [selectedKeys, setSelectedKeys] = React.useState<Set<string>>(
    new Set(),
  );
  const [selectedUserIds, setSelectedUserIds] = React.useState<Set<string>>(
    new Set(),
  );
  const [filterValue, setFilterValue] = React.useState<FilterValue>("all");
  const [userPopoverOpen, setUserPopoverOpen] = React.useState(false);
  const [keyStatuses, setKeyStatuses] = React.useState<Map<string, KeyStatus>>(
    new Map(),
  );
  const [step, setStep] = React.useState<"resolve" | "review">("resolve");
  const [incompleteDialogOpen, setIncompleteDialogOpen] = React.useState(false);

  const allData = React.useMemo<Array<AffectedUserGroup>>(
    () =>
      pending ? groupAffectedKeysByUser(pending.conflict.affected_keys) : [],
    [pending],
  );

  const allKeyIdsGlobal = React.useMemo(
    () => allData.flatMap((u) => u.keys.map((k) => k.key_id)),
    [allData],
  );

  const resolvedCount = allKeyIdsGlobal.filter((id) =>
    keyStatuses.has(id),
  ).length;
  const totalKeysGlobal = allKeyIdsGlobal.length;
  const unresolvedCount = totalKeysGlobal - resolvedCount;

  const replayMutation = useMutation({
    mutationFn: async (resolutions: Array<GatewayKeyResolution>) => {
      if (!pending) throw new Error("No pending policy conflict");
      return replayPendingPolicyConflict(pending, resolutions);
    },
    async onSuccess(result) {
      if (result.status === "conflict") {
        setPending(result.pending);
        setKeyStatuses(new Map());
        setSelectedKeys(new Set());
        setStep("resolve");
        toast.error(result.pending.conflict.message);
        return;
      }

      const returnUrl = pending?.returnUrl ?? "/policies";
      const successToast = pending?.successToast;
      clearPendingPolicyConflict();
      setPending(null);
      qc.invalidateQueries({ queryKey: ["policies"] });
      qc.invalidateQueries({ queryKey: ["users"] });
      qc.invalidateQueries({ queryKey: ["groups"] });
      toast.success(successToast ?? "Policy saved successfully");
      router.history.push(returnUrl);
    },
    async onError(err) {
      let message = err.message;
      if (err instanceof HTTPError) {
        const body = await err.response.json().catch(() => null);
        if (body?.detail) {
          message =
            typeof body.detail === "string"
              ? body.detail
              : (body.detail.message ?? message);
        }
      }
      toast.error(`Failed to apply resolutions: ${message}`);
    },
  });

  if (!pending) return null;

  function handleReviewChanges() {
    if (unresolvedCount > 0) {
      setIncompleteDialogOpen(true);
      return;
    }
    setStep("review");
  }

  function handleSkipAndContinue() {
    setKeyStatuses((prev) => {
      const next = new Map(prev);
      for (const user of allData) {
        for (const key of user.keys) {
          if (!next.has(key.key_id)) {
            next.set(key.key_id, "skipped");
          }
        }
      }
      return next;
    });
    setIncompleteDialogOpen(false);
    setStep("review");
  }

  function handleApply() {
    const resolutions: Array<GatewayKeyResolution> = [];
    for (const user of allData) {
      for (const key of user.keys) {
        const status = keyStatuses.get(key.key_id) ?? "skipped";
        resolutions.push({
          key_id: key.key_id,
          action: STATUS_TO_ACTION[status],
        });
      }
    }
    replayMutation.mutate(resolutions);
  }

  function handleCancel() {
    const returnUrl = pending?.returnUrl ?? "/policies";
    clearPendingPolicyConflict();
    setPending(null);
    router.history.push(returnUrl);
  }

  const data = (() => {
    let filtered = allData;
    if (selectedUserIds.size > 0)
      filtered = filtered.filter((u) => selectedUserIds.has(u.id));

    if (filterValue === "needs-action")
      filtered = filtered
        .map((u) => ({
          ...u,
          keys: u.keys.filter((k) => !keyStatuses.has(k.key_id)),
        }))
        .filter((u) => u.keys.length > 0);
    else if (filterValue === "completed")
      filtered = filtered
        .map((u) => ({
          ...u,
          keys: u.keys.filter((k) => keyStatuses.has(k.key_id)),
        }))
        .filter((u) => u.keys.length > 0);

    return filtered;
  })();

  const allKeyIds = data.flatMap((u) => u.keys.map((k) => k.key_id));
  const totalKeys = data.reduce((s, u) => s + u.keys.length, 0);
  const totalEntitiesAffected = data.reduce(
    (s, u) => s + u.keys.reduce((s2, k) => s2 + k.missing_entities.length, 0),
    0,
  );
  const allSelected =
    selectedKeys.size === allKeyIds.length && allKeyIds.length > 0;

  function toggleKey(keyId: string) {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(keyId)) next.delete(keyId);
      else next.add(keyId);
      return next;
    });
  }

  function toggleAll() {
    if (allSelected) setSelectedKeys(new Set());
    else setSelectedKeys(new Set(allKeyIds));
  }

  function applyStatusToSelected(status: KeyStatus) {
    setKeyStatuses((prev) => {
      const next = new Map(prev);
      for (const keyId of selectedKeys) {
        next.set(keyId, status);
      }
      return next;
    });
    setSelectedKeys(new Set());
  }

  function setStatusForKey(keyId: string, status: KeyStatus | undefined) {
    setKeyStatuses((prev) => {
      const next = new Map(prev);
      if (status === undefined) next.delete(keyId);
      else next.set(keyId, status);
      return next;
    });
  }

  function toggleUserFilter(userId: string) {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  }

  const userDropdownLabel =
    selectedUserIds.size === 0
      ? "Users"
      : selectedUserIds.size === 1
        ? (allData.find((u) => selectedUserIds.has(u.id))?.fullName ?? "Users")
        : `${selectedUserIds.size} Users`;

  const FILTER_OPTIONS: Array<{ value: FilterValue; label: string }> = [
    { value: "all", label: "All items" },
    { value: "needs-action", label: "Needs action" },
    { value: "completed", label: "Resolved" },
  ];

  if (step === "review") {
    return (
      <ReviewStep
        allData={allData}
        keyStatuses={keyStatuses}
        onBack={() => setStep("resolve")}
        onApply={handleApply}
        applying={replayMutation.isPending}
        policyName={pending.apiRequest.label}
        followUpCount={pending.followUps?.length ?? 0}
      />
    );
  }

  return (
    <div className="p-4 md:p-6">
      <div className="mb-4 flex items-center gap-1 text-sm md:mb-6">
        <Link
          to="/policies"
          className="font-medium text-primary hover:underline"
        >
          Policies
        </Link>
        <ChevronRightIcon className="size-4 text-muted-foreground" />
        <span className="font-medium text-muted-foreground">
          Resolve conflicts
        </span>
      </div>

      <div className="mb-4 flex flex-col gap-1">
        <h1 className="text-2xl font-semibold">
          Resolve gateway key conflicts
        </h1>
        <p className="text-sm text-muted-foreground">
          {pending.apiRequest.label ? (
            <>
              Policy{" "}
              <span className="font-medium text-foreground">
                "{pending.apiRequest.label}"
              </span>{" "}
              affects the following gateway keys. Pick an action for each key to
              continue.
            </>
          ) : (
            <>
              This policy affects the following gateway keys. Pick an action for
              each key to continue.
            </>
          )}
          {pending.followUps && pending.followUps.length > 0 && (
            <>
              {" "}
              <span className="font-medium text-foreground">
                {pending.followUps.length} more request
                {pending.followUps.length === 1 ? "" : "s"}
              </span>{" "}
              will run automatically after this one is resolved.
            </>
          )}
        </p>
      </div>

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
        <div className="flex gap-2">
          <Popover open={userPopoverOpen} onOpenChange={setUserPopoverOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={userPopoverOpen}
                className="w-40 justify-between font-normal sm:w-44"
              >
                <span className="truncate">{userDropdownLabel}</span>
                <ChevronDownIcon className="size-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56 p-0" align="start">
              <Command>
                <CommandInput placeholder="Search..." />
                <CommandList>
                  <CommandEmpty>No users found.</CommandEmpty>
                  <CommandGroup>
                    {allData.map((user) => (
                      <CommandItem
                        key={user.id}
                        value={user.fullName}
                        onSelect={() => toggleUserFilter(user.id)}
                      >
                        <CheckIcon
                          className={cn(
                            "size-4",
                            selectedUserIds.has(user.id)
                              ? "opacity-100"
                              : "opacity-0",
                          )}
                        />
                        {user.fullName}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>

          <Select
            value={filterValue}
            onValueChange={(v) => setFilterValue(v as FilterValue)}
          >
            <SelectTrigger className="w-36 sm:w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FILTER_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
          <Badge
            variant="outline"
            className="gap-1.5 px-2.5 py-1 text-xs font-normal"
          >
            <UsersIcon className="size-3.5 text-primary" />
            {data.length} Users affected
          </Badge>
          <Badge
            variant="outline"
            className="gap-1.5 px-2.5 py-1 text-xs font-normal"
          >
            <KeyRoundIcon className="size-3.5 text-primary" />
            {totalKeys} Keys affected
          </Badge>
          <Badge
            variant="outline"
            className="gap-1.5 px-2.5 py-1 text-xs font-normal"
          >
            <ShieldAlertIcon className="size-3.5 text-primary" />
            {totalEntitiesAffected} Missing entities
          </Badge>
        </div>
      </div>

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <Checkbox
            checked={allSelected}
            onCheckedChange={toggleAll}
            aria-label="Select all keys"
          />
          Select all ({selectedKeys.size} of {totalKeys} keys selected)
        </label>

        <div className="flex items-center gap-2">
          <Button
            size="sm"
            className="bg-[#099250] text-white hover:bg-emerald-700"
            disabled={selectedKeys.size === 0}
            onClick={() => applyStatusToSelected("propagated")}
          >
            Propagate
          </Button>
          <Button
            size="sm"
            variant="destructive"
            disabled={selectedKeys.size === 0}
            onClick={() => applyStatusToSelected("revoked")}
          >
            Revoke
          </Button>
          <Button
            className="border-orange-300 text-orange-300"
            size="sm"
            variant="outline"
            disabled={selectedKeys.size === 0}
            onClick={() => applyStatusToSelected("skipped")}
          >
            Skip
          </Button>
        </div>
      </div>

      <Separator className="mb-4 md:mb-6" />

      <div className="space-y-4 md:space-y-6">
        {data.map((user) => (
          <UserSection
            key={user.id}
            user={user}
            selectedKeys={selectedKeys}
            onToggleKey={toggleKey}
            keyStatuses={keyStatuses}
            onSetStatusForKey={setStatusForKey}
          />
        ))}
        {data.length === 0 && (
          <p className="py-12 text-center text-sm text-muted-foreground">
            No conflicts match the current filters.
          </p>
        )}
      </div>

      <div className="mt-6 flex flex-col-reverse gap-3 border-t pt-6 sm:flex-row sm:items-center sm:justify-end md:mt-8">
        <Button
          variant="outline"
          className="w-full sm:w-auto"
          onClick={handleCancel}
          disabled={replayMutation.isPending}
        >
          Cancel
        </Button>
        <Button
          variant="destructive"
          className="w-full sm:w-auto"
          onClick={handleReviewChanges}
          disabled={replayMutation.isPending || totalKeysGlobal === 0}
        >
          Review changes
        </Button>
      </div>

      <Dialog
        open={incompleteDialogOpen}
        onOpenChange={setIncompleteDialogOpen}
      >
        <DialogContent className="max-w-md">
          <DialogHeader className="items-center sm:items-center">
            <div className="mb-2 flex size-16 items-center justify-center rounded-full bg-orange-100">
              <div className="flex size-10 items-center justify-center rounded-full bg-orange-200">
                <CircleAlertIcon className="size-6 text-orange-600" />
              </div>
            </div>
            <DialogTitle>Incomplete selection</DialogTitle>
            <DialogDescription className="text-center">
              You've selected actions for {resolvedCount} of {totalKeysGlobal}{" "}
              keys. What would you like to do with the remaining{" "}
              {unresolvedCount === 1 ? "key" : `${unresolvedCount} keys`}?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-2 sm:justify-center">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setIncompleteDialogOpen(false)}
            >
              Go back and fix
            </Button>
            <Button className="flex-1" onClick={handleSkipAndContinue}>
              Skip & continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function UserSection({
  user,
  selectedKeys,
  onToggleKey,
  keyStatuses,
  onSetStatusForKey,
}: {
  user: AffectedUserGroup;
  selectedKeys: Set<string>;
  onToggleKey: (keyId: string) => void;
  keyStatuses: Map<string, KeyStatus>;
  onSetStatusForKey: (keyId: string, status: KeyStatus | undefined) => void;
}) {
  const totalEntities = user.keys.reduce(
    (s, k) => s + k.missing_entities.length,
    0,
  );
  const resolvedCount = user.keys.filter((k) =>
    keyStatuses.has(k.key_id),
  ).length;

  const userKeyIds = user.keys.map((k) => k.key_id);
  const allUserKeysSelected =
    userKeyIds.length > 0 && userKeyIds.every((id) => selectedKeys.has(id));
  const someUserKeysSelected =
    !allUserKeysSelected && userKeyIds.some((id) => selectedKeys.has(id));

  function toggleUserKeys() {
    for (const id of userKeyIds) {
      if (allUserKeysSelected) {
        if (selectedKeys.has(id)) onToggleKey(id);
      } else {
        if (!selectedKeys.has(id)) onToggleKey(id);
      }
    }
  }

  return (
    <div className="rounded-lg border">
      <div className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5 sm:py-4">
        <div className="flex items-center gap-3">
          <Checkbox
            checked={
              allUserKeysSelected
                ? true
                : someUserKeysSelected
                  ? "indeterminate"
                  : false
            }
            onCheckedChange={toggleUserKeys}
            aria-label={`Select all keys for ${user.fullName}`}
          />
          <Avatar className="size-9">
            <AvatarFallback className="text-xs">
              {getInitials(user.fullName)}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold">{user.fullName}</p>
            <p className="truncate text-xs text-muted-foreground">
              {user.email}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 pl-11 sm:pl-0">
          <Badge variant="outline" className="text-xs font-normal">
            {user.keys.length} {user.keys.length === 1 ? "key" : "keys"}
          </Badge>
          <Badge variant="outline" className="text-xs font-normal">
            {totalEntities}{" "}
            {totalEntities === 1 ? "missing entity" : "missing entities"}
          </Badge>
          <Badge
            variant="outline"
            className={cn(
              "text-xs font-normal",
              resolvedCount === user.keys.length &&
                "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-400",
            )}
          >
            {resolvedCount}/{user.keys.length} resolved
          </Badge>
        </div>
      </div>

      <div className="border-t">
        {user.keys.map((key) => (
          <KeySection
            key={key.key_id}
            gatewayKey={key}
            selected={selectedKeys.has(key.key_id)}
            onToggle={() => onToggleKey(key.key_id)}
            status={keyStatuses.get(key.key_id)}
            onStatusChange={(s) => onSetStatusForKey(key.key_id, s)}
          />
        ))}
      </div>
    </div>
  );
}

const KEY_STATUS_STYLES: Record<KeyStatus, string> = {
  propagated:
    "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-400",
  revoked:
    "border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-400",
  skipped:
    "border-orange-200 bg-orange-50 text-orange-700 dark:border-orange-800 dark:bg-orange-950 dark:text-orange-400",
};

const KEY_STATUS_LABELS: Record<KeyStatus, string> = {
  propagated: "Propagate",
  revoked: "Revoke",
  skipped: "Skip",
};

function KeySection({
  gatewayKey,
  selected,
  onToggle,
  status,
  onStatusChange,
}: {
  gatewayKey: AffectedKey;
  selected: boolean;
  onToggle: () => void;
  status?: KeyStatus;
  onStatusChange: (status: KeyStatus | undefined) => void;
}) {
  const [open, setOpen] = React.useState(true);

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="border-b last:border-b-0">
        <div className="flex flex-col gap-2 bg-muted/30 px-4 py-3 sm:flex-row sm:items-center sm:gap-3 sm:px-5">
          <div className="flex items-center gap-3">
            <Checkbox
              checked={selected}
              onCheckedChange={onToggle}
              aria-label={`Select ${gatewayKey.key_name}`}
            />
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="flex min-w-0 flex-1 items-center gap-2 text-left"
              >
                <ChevronDownIcon
                  className={cn(
                    "size-4 shrink-0 text-muted-foreground transition-transform",
                    !open && "-rotate-90",
                  )}
                />
                <KeyRoundIcon className="size-4 shrink-0 text-muted-foreground" />
                <span className="truncate text-sm font-medium">
                  {gatewayKey.key_name}
                  {gatewayKey.key_description && (
                    <span className="text-muted-foreground">
                      {" "}
                      - {gatewayKey.key_description}
                    </span>
                  )}
                </span>
                {status && (
                  <Badge
                    variant="outline"
                    className={cn(
                      "ml-2 shrink-0 text-xs font-normal",
                      KEY_STATUS_STYLES[status],
                    )}
                  >
                    {KEY_STATUS_LABELS[status]}
                  </Badge>
                )}
              </button>
            </CollapsibleTrigger>
          </div>
          <div className="flex shrink-0 items-center gap-2 pl-11 sm:ml-auto sm:pl-0">
            <Badge variant="outline" className="text-xs font-normal">
              {gatewayKey.missing_entities.length}{" "}
              {gatewayKey.missing_entities.length === 1
                ? "missing entity"
                : "missing entities"}
            </Badge>
            <Select
              value={status ?? ""}
              onValueChange={(v) => onStatusChange(v as KeyStatus)}
            >
              <SelectTrigger className="h-8 w-36 text-xs">
                <SelectValue placeholder="Choose action" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="propagated">Propagate</SelectItem>
                <SelectItem value="revoked">Revoke</SelectItem>
                <SelectItem value="skipped">Skip</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <CollapsibleContent>
          <div className="px-4 py-3 sm:px-5">
            <p className="mb-2 text-xs text-muted-foreground">
              Missing entities required by this policy:
            </p>
            <div className="flex flex-wrap gap-1.5">
              {gatewayKey.missing_entities.map((e) => (
                <MissingEntityBadge key={e.id} entity={e} />
              ))}
            </div>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

function MissingEntityBadge({ entity }: { entity: AffectedEntity }) {
  return (
    <Badge
      variant="outline"
      className="border-red-200 bg-red-50 text-xs font-normal text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-400"
    >
      {entity.name}
    </Badge>
  );
}

interface ReviewGroupUser {
  user: AffectedUserGroup;
  keys: Array<AffectedKey>;
}

interface ReviewGroup {
  status: KeyStatus;
  users: Array<ReviewGroupUser>;
}

function groupDataByStatus(
  allData: Array<AffectedUserGroup>,
  keyStatuses: Map<string, KeyStatus>,
): Array<ReviewGroup> {
  const groups: Record<KeyStatus, Array<ReviewGroupUser>> = {
    propagated: [],
    revoked: [],
    skipped: [],
  };

  for (const user of allData) {
    const byStatus: Record<KeyStatus, Array<AffectedKey>> = {
      propagated: [],
      revoked: [],
      skipped: [],
    };

    for (const key of user.keys) {
      const status = keyStatuses.get(key.key_id);
      if (status) byStatus[status].push(key);
    }

    for (const s of ["propagated", "revoked", "skipped"] as Array<KeyStatus>) {
      if (byStatus[s].length > 0) {
        groups[s].push({ user, keys: byStatus[s] });
      }
    }
  }

  return (["propagated", "revoked", "skipped"] as Array<KeyStatus>)
    .filter((s) => groups[s].length > 0)
    .map((s) => ({ status: s, users: groups[s] }));
}

const REVIEW_SECTION_CONFIG: Record<
  KeyStatus,
  {
    title: string;
    description: string;
    descColor: string;
    icon: React.ComponentType<{ className?: string }>;
    borderColor: string;
    iconColor: string;
    titleColor: string;
    badgeClass: string;
    bgTint: string;
  }
> = {
  propagated: {
    title: "Propagate",
    description: "Add the missing entities to these keys",
    descColor: "text-[#087443]",
    icon: CheckIcon,
    borderColor: "border-l-[#087443]",
    iconColor: "text-[#087443]",
    titleColor: "text-[#087443]",
    badgeClass: "border-emerald-200 bg-emerald-50 text-[#087443]",
    bgTint: "bg-emerald-50/30",
  },
  revoked: {
    title: "Revoke",
    description: "These keys will be permanently deleted",
    descColor: "text-[#B42318]",
    icon: Trash2Icon,
    borderColor: "border-l-[#B42318]",
    iconColor: "text-[#B42318]",
    titleColor: "text-[#B42318]",
    badgeClass: "border-red-200 bg-red-50 text-[#B42318]",
    bgTint: "bg-red-50/30",
  },
  skipped: {
    title: "Skip",
    description: "These keys will remain non-compliant",
    descColor: "text-[#F79009]",
    icon: Undo2Icon,
    borderColor: "border-l-[#F79009]",
    iconColor: "text-[#F79009]",
    titleColor: "text-[#F79009]",
    badgeClass: "border-orange-200 bg-orange-50 text-[#F79009]",
    bgTint: "bg-orange-50/30",
  },
};

function ReviewStep({
  allData,
  keyStatuses,
  onBack,
  onApply,
  applying,
  policyName,
  followUpCount,
}: {
  allData: Array<AffectedUserGroup>;
  keyStatuses: Map<string, KeyStatus>;
  onBack: () => void;
  onApply: () => void;
  applying: boolean;
  policyName?: string;
  followUpCount?: number;
}) {
  const groups = React.useMemo(
    () => groupDataByStatus(allData, keyStatuses),
    [allData, keyStatuses],
  );

  const statusCounts = React.useMemo(() => {
    const counts: Record<KeyStatus, number> = {
      propagated: 0,
      revoked: 0,
      skipped: 0,
    };
    for (const [, status] of keyStatuses) {
      counts[status]++;
    }
    return counts;
  }, [keyStatuses]);

  return (
    <div className="p-4 md:p-6">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between md:mb-6">
        <div className="flex items-center gap-1 text-sm">
          <Link
            to="/policies"
            className="font-medium text-primary hover:underline"
          >
            Policies
          </Link>
          <ChevronRightIcon className="size-4 text-muted-foreground" />
          <button
            type="button"
            onClick={onBack}
            className="font-medium text-primary hover:underline"
          >
            Resolve conflicts
          </button>
          <ChevronRightIcon className="size-4 text-muted-foreground" />
          <span className="font-medium text-muted-foreground">
            Review changes
          </span>
        </div>
        <div className="flex items-center gap-2">
          {statusCounts.propagated > 0 && (
            <Badge
              variant="outline"
              className="gap-1.5 border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700"
            >
              <CircleCheckIcon className="size-3.5" />
              {statusCounts.propagated} Propagate
            </Badge>
          )}
          {statusCounts.revoked > 0 && (
            <Badge
              variant="outline"
              className="gap-1.5 border-red-200 bg-red-50 px-2.5 py-1 text-xs font-medium text-red-700"
            >
              <BanIcon className="size-3.5" />
              {statusCounts.revoked} Revoke
            </Badge>
          )}
          {statusCounts.skipped > 0 && (
            <Badge
              variant="outline"
              className="gap-1.5 border-orange-200 bg-orange-50 px-2.5 py-1 text-xs font-medium text-orange-700"
            >
              <RotateCcwIcon className="size-3.5" />
              {statusCounts.skipped} Skip
            </Badge>
          )}
        </div>
      </div>

      {policyName && (
        <div className="mb-4 flex flex-col gap-1">
          <h1 className="text-2xl font-semibold">Review changes</h1>
          <p className="text-sm text-muted-foreground">
            Applying resolutions for policy{" "}
            <span className="font-medium text-foreground">"{policyName}"</span>{" "}
            {followUpCount && followUpCount > 0 ? (
              <>
                . {followUpCount} additional request
                {followUpCount === 1 ? "" : "s"} will run after this succeeds.
              </>
            ) : null}
          </p>
        </div>
      )}

      <div className="space-y-6">
        {groups.map((group) => (
          <ReviewSection key={group.status} group={group} />
        ))}
      </div>

      <div className="mt-6 flex items-center justify-end gap-3 border-t pt-6 md:mt-8">
        <Button variant="outline" onClick={onBack} disabled={applying}>
          Back
        </Button>
        <Button onClick={onApply} disabled={applying}>
          {applying ? "Applying..." : "Apply & reapply policy"}
        </Button>
      </div>
    </div>
  );
}

function ReviewSection({ group }: { group: ReviewGroup }) {
  const config = REVIEW_SECTION_CONFIG[group.status];
  const Icon = config.icon;

  return (
    <div
      className={cn(
        "overflow-hidden rounded-xl border border-l-4",
        config.borderColor,
        config.bgTint,
      )}
    >
      <div className="px-5 pt-5 pb-2">
        <div className="flex items-center gap-2">
          <Icon className={cn("size-5", config.iconColor)} />
          <span className={cn("font-semibold", config.titleColor)}>
            {config.title}
          </span>
          <span
            className={cn("text-sm text-muted-foreground", config.descColor)}
          >
            · {config.description}
          </span>
        </div>
      </div>

      <div className="space-y-5 px-5 pb-5">
        {group.users.map(({ user, keys }) => (
          <ReviewUserBlock
            key={user.id}
            user={user}
            keys={keys}
            status={group.status}
          />
        ))}
      </div>
    </div>
  );
}

function ReviewUserBlock({
  user,
  keys,
  status,
}: {
  user: AffectedUserGroup;
  keys: Array<AffectedKey>;
  status: KeyStatus;
}) {
  const config = REVIEW_SECTION_CONFIG[status];

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Avatar className="size-9">
            <AvatarFallback className="text-xs">
              {getInitials(user.fullName)}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold">{user.fullName}</p>
            <p className="truncate text-xs text-muted-foreground">
              {user.email}
            </p>
          </div>
        </div>
        <Badge
          variant="outline"
          className={cn("text-xs font-medium", config.badgeClass)}
        >
          {keys.length} {KEY_STATUS_LABELS[status]}
        </Badge>
      </div>

      <div className="space-y-3 pl-12">
        {keys.map((key) => (
          <ReviewKeyCard key={key.key_id} gatewayKey={key} />
        ))}
      </div>
    </div>
  );
}

function ReviewKeyCard({ gatewayKey }: { gatewayKey: AffectedKey }) {
  return (
    <div className="overflow-hidden rounded-lg border bg-background">
      <div className="flex flex-col gap-2 bg-muted/30 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <KeyRoundIcon className="size-4 shrink-0 text-muted-foreground" />
          <span className="truncate text-sm font-medium">
            {gatewayKey.key_name}
            {gatewayKey.key_description && (
              <span className="text-muted-foreground">
                {" "}
                - {gatewayKey.key_description}
              </span>
            )}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Badge variant="outline" className="text-xs font-normal">
            {gatewayKey.missing_entities.length}{" "}
            {gatewayKey.missing_entities.length === 1
              ? "missing entity"
              : "missing entities"}
          </Badge>
        </div>
      </div>

      <div className="px-4 py-3">
        <div className="flex flex-wrap gap-1.5">
          {gatewayKey.missing_entities.map((e) => (
            <MissingEntityBadge key={e.id} entity={e} />
          ))}
        </div>
      </div>
    </div>
  );
}

// Re-export for compatibility with callers that might still reference it.
export { ACTION_TO_STATUS };
